import React, { useState, useEffect } from 'react';

const API_BASE = import.meta?.env?.VITE_API_URL || 'http://45.32.118.117:8090';

const MonthlyPerformance = ({ accountId }) => {
  const [monthlyData, setMonthlyData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [availableYears, setAvailableYears] = useState([]);

  useEffect(() => {
    if (!accountId) return;
    fetchMonthlyPerformance();
  }, [accountId]);

  const fetchMonthlyPerformance = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token') || localStorage.getItem('access_token');
      
      const response = await fetch(
        `${API_BASE}/api/trader/monthly-performance?account_id=${accountId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      const months = data.months || [];
      setMonthlyData(months);
      
      // Extract available years
      const years = [...new Set(months.map(m => m.year))].sort((a, b) => b - a);
      setAvailableYears(years);
      
      // Set default to current year if available, otherwise latest year
      const currentYear = new Date().getFullYear();
      if (years.includes(currentYear)) {
        setSelectedYear(currentYear);
      } else if (years.length > 0) {
        setSelectedYear(years[0]);
      }
    } catch (err) {
      console.error('Failed to fetch monthly performance:', err);
      setMonthlyData([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        padding: 16,
        background: '#1a1a1a',
        borderRadius: 12,
        marginBottom: 24,
        color: '#6b7280'
      }}>
        Loading monthly performance...
      </div>
    );
  }

  if (!monthlyData || monthlyData.length === 0) {
    return (
      <div style={{
        padding: 16,
        background: '#1a1a1a',
        borderRadius: 12,
        marginBottom: 24,
        color: '#6b7280'
      }}>
        No monthly data available
      </div>
    );
  }

  // Filter data for selected year
  const yearData = monthlyData.filter(m => m.year === selectedYear);
  
  if (yearData.length === 0) {
    return (
      <div style={{ marginBottom: 24 }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 12
        }}>
          <h3 style={{
            fontSize: 16,
            fontWeight: 600,
            color: '#fff',
            margin: 0
          }}>
            Monthly Performance
          </h3>
          
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            style={{
              padding: '6px 12px',
              background: '#1a1a1a',
              border: '1px solid #333',
              borderRadius: 6,
              color: '#fff',
              fontSize: 14,
              cursor: 'pointer'
            }}
          >
            {availableYears.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>
        
        <div style={{
          padding: 16,
          background: '#1a1a1a',
          borderRadius: 12,
          color: '#6b7280'
        }}>
          No data for {selectedYear}
        </div>
      </div>
    );
  }

  const maxProfit = Math.max(...yearData.map(m => Math.abs(m.profit)));

  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 12
      }}>
        <h3 style={{
          fontSize: 16,
          fontWeight: 600,
          color: '#fff',
          margin: 0
        }}>
          Monthly Performance
        </h3>
        
        <select
          value={selectedYear}
          onChange={(e) => setSelectedYear(parseInt(e.target.value))}
          style={{
            padding: '6px 12px',
            background: '#1a1a1a',
            border: '1px solid #333',
            borderRadius: 6,
            color: '#fff',
            fontSize: 14,
            cursor: 'pointer'
          }}
        >
          {availableYears.map(year => (
            <option key={year} value={year}>{year}</option>
          ))}
        </select>
      </div>
      
      <div style={{
        padding: 16,
        background: '#1a1a1a',
        borderRadius: 12
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: 8,
          height: 200
        }}>
          {yearData.map((month, idx) => {
            const barHeight = maxProfit > 0 
              ? (Math.abs(month.profit) / maxProfit) * 180 
              : 0;
            const isProfit = month.profit >= 0;

            return (
              <div
                key={idx}
                style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 4
                }}
              >
                <div
                  style={{
                    width: '100%',
                    height: barHeight || 4,
                    background: isProfit ? '#10b981' : '#ef4444',
                    borderRadius: 4,
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                  title={`${month.month} ${month.year}: $${month.profit.toFixed(2)}`}
                />
                <div style={{
                  fontSize: 11,
                  color: '#9ca3af',
                  fontWeight: 500,
                  textAlign: 'center'
                }}>
                  {month.month.substring(0, 3)}
                </div>
              </div>
            );
          })}
        </div>

        <div style={{
          marginTop: 16,
          paddingTop: 12,
          borderTop: '1px solid #333',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
          gap: 12,
          fontSize: 12,
          color: '#9ca3af'
        }}>
          <div>
            <span style={{ color: '#6b7280' }}>Total Months:</span>{' '}
            <span style={{ color: '#fff', fontWeight: 600 }}>{yearData.length}</span>
          </div>
          <div>
            <span style={{ color: '#6b7280' }}>Profitable:</span>{' '}
            <span style={{ color: '#10b981', fontWeight: 600 }}>
              {yearData.filter(m => m.profit > 0).length}
            </span>
          </div>
          <div>
            <span style={{ color: '#6b7280' }}>Loss:</span>{' '}
            <span style={{ color: '#ef4444', fontWeight: 600 }}>
              {yearData.filter(m => m.profit < 0).length}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MonthlyPerformance;
